// Builds the display-ready model for a shareable comparison card.
//
// Everything a template renders is produced here, ALREADY formatted and ALREADY
// redacted for privacy. Templates never see a raw number and never format one,
// so "the coach hid the numbers" and "the client never logged a weight" are the
// same case for them: the label is null and the slot collapses.

const MINUS = '−';
const MS_PER_DAY = 86400000;

// Card wording. English is the default so this module stays usable — and
// testable — without an i18n runtime. ComparisonShareDialog passes localised
// values in. Templates read model.labels and never hold a literal, so a new
// template cannot reintroduce a hardcoded English string.
export const DEFAULT_CARD_LABELS = {
  before: 'BEFORE',
  after: 'AFTER',
  transformation: 'TRANSFORMATION',
  start: 'START',
  now: 'NOW',
  bodyFat: 'BODY FAT',
  bodyFatShort: '% BF',
  // Units elapsedLabel() composes with a number ("N days", "N weeks", ...).
  // English defaults so this module stays i18n-free; ComparisonShareDialog
  // supplies translated ones.
  day: 'day',
  days: 'days',
  week: 'week',
  weeks: 'weeks',
  month: 'month',
  months: 'months',
  // Locale for dateLabel()'s toLocaleDateString call — 'en-GB' default keeps
  // the DD MMM YYYY shape this module always used.
  locale: 'en-GB',
  // Pose id -> display name, supplied by ComparisonShareDialog. Empty by
  // default so this module stays i18n-free.
  poseNames: {},
};

export function elapsedLabel(fromISO, toISO, labels = DEFAULT_CARD_LABELS) {
  if (!fromISO || !toISO) return null;
  const from = new Date(fromISO);
  const to = new Date(toISO);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;

  const days = Math.round((to - from) / MS_PER_DAY);
  if (days < 1) return null;
  if (days < 14) return `${days} ${days === 1 ? labels.day : labels.days}`;

  const weeks = Math.round(days / 7);
  if (weeks <= 52) return `${weeks} ${labels.weeks}`;

  return `${Math.round(days / 30.44)} ${labels.months}`;
}

export function formatSigned(value, digits = 1) {
  const v = Number(value);
  if (!Number.isFinite(v)) return '';
  const magnitude = Math.abs(v).toFixed(digits);
  if (v > 0) return `+${magnitude}`;
  if (v < 0) return `${MINUS}${magnitude}`;
  return magnitude;
}

function delta(from, to) {
  if (from == null || from === '' || to == null || to === '') return null;
  const d = Number(to) - Number(from);
  return Number.isFinite(d) ? d : null;
}

function num(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function dateLabel(iso, locale = DEFAULT_CARD_LABELS.locale) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
}

function handleLabelFrom(handle) {
  if (!handle) return null;
  const slug = String(handle).replace(/\s+/g, '').toLowerCase();
  return slug ? `@${slug}` : null;
}

// Only non-empty strings override a default. A missing or blank translation
// must fall back to English rather than baking an empty tag into an exported
// image the user cannot edit after sharing.
function mergeLabels(overrides) {
  const merged = { ...DEFAULT_CARD_LABELS };
  for (const [key, value] of Object.entries(overrides || {})) {
    // poseNames is the one non-string label: a map of pose id -> display name.
    // Merged rather than replaced so a partial map still falls back per pose.
    if (key === 'poseNames' && value && typeof value === 'object') {
      merged.poseNames = { ...merged.poseNames, ...value };
      continue;
    }
    if (typeof value === 'string' && value.trim() !== '') merged[key] = value;
  }
  return merged;
}

export function buildCardModel({
  pairs = null,
  // Legacy flat inputs — still accepted so nothing outside the share dialog
  // has to change at once.
  beforePhotoUrl, afterPhotoUrl,
  beforeCanonical, afterCanonical,
  manualBefore, manualAfter,
  beforeBackdropUrl = null, afterBackdropUrl = null,
  beforeNatural = null, afterNatural = null,
  beforeDate, afterDate,
  beforeWeightKg, beforeBodyFat,
  afterWeightKg, afterBodyFat,
  poseType = '',
  sliderPos = 50,
  handle = '',
  labels = null,
  privacy = { showNumbers: true, showHandle: true },
}) {
  const showNumbers = privacy.showNumbers !== false;
  const L = mergeLabels(labels);
  const elapsed = elapsedLabel(beforeDate, afterDate, L);

  const side = (slot, date, weight, bodyFat) => {
    const w = num(weight);
    const bf = num(bodyFat);
    return {
      url: slot.url,
      backdropUrl: slot.backdropUrl ?? null,
      canonical: slot.canonical,
      manual: slot.manual,
      natural: slot.natural ?? null,
      dateLabel: dateLabel(date, L.locale),
      weightLabel: showNumbers && w != null ? `${w.toFixed(1)}kg` : null,
      bodyFatLabel: showNumbers && bf != null ? `${bf.toFixed(1)}%` : null,
    };
  };

  const poseName = (id) => L.poseNames?.[id] || String(id || '').replace(/_/g, ' ').toUpperCase();
  const poseLabelFor = (poses) => {
    const b = poseName(poses?.before);
    const a = poseName(poses?.after);
    return b === a ? b : `${b} → ${a}`;
  };

  // One list of pairs whether the caller passed `pairs` or the legacy flat
  // props, so everything below has exactly one shape to reason about.
  const slotPairs = pairs && pairs.length
    ? pairs
    : [{
        poseType: { before: poseType, after: poseType },
        before: { url: beforePhotoUrl, backdropUrl: beforeBackdropUrl, canonical: beforeCanonical, manual: manualBefore, natural: beforeNatural },
        after: { url: afterPhotoUrl, backdropUrl: afterBackdropUrl, canonical: afterCanonical, manual: manualAfter, natural: afterNatural },
      }];

  const builtPairs = slotPairs.map((p) => ({
    before: side(p.before, beforeDate, beforeWeightKg, beforeBodyFat),
    after: side(p.after, afterDate, afterWeightKg, afterBodyFat),
    poseLabel: poseLabelFor(p.poseType),
  }));

  const weightDelta = delta(beforeWeightKg, afterWeightKg);
  const bodyFatDelta = delta(beforeBodyFat, afterBodyFat);
  const poseLabel = builtPairs[0].poseLabel;

  // The hero never renders empty. Weight delta first; elapsed time next; the
  // pose last, so even a card with no numbers and no dates says something.
  const hero = showNumbers && weightDelta != null
    ? { value: formatSigned(weightDelta), unit: 'kg', sub: elapsed || poseLabel }
    : { value: elapsed || poseLabel || L.transformation, unit: '', sub: poseLabel };

  return {
    pairs: builtPairs,
    before: builtPairs[0].before,
    after: builtPairs[0].after,
    hero,
    elapsedLabel: elapsed,
    bodyFatDeltaLabel: showNumbers && bodyFatDelta != null ? formatSigned(bodyFatDelta) : null,
    poseLabel,
    sliderPos,
    handleLabel: privacy.showHandle === false ? null : handleLabelFrom(handle),
    showNumbers,
    labels: L,
  };
}
